import { useEffect, useState } from "react";

// src/config/HomeVideos.js
//
// Vidéos de fond de la page d'accueil. Deux listes : une pour les écrans
// larges (vidéos horizontales), une pour les téléphones (vidéos verticales).
// Une seule vidéo est tirée au sort par liste, à chaque chargement de page.
//
// ═══════════════════════════════════════════════════════════════
//  POUR AJOUTER OU RETIRER UNE VIDÉO
//  1. Déposer le fichier .mp4 dans  public/videos/
//  2. Ajouter (ou retirer) son nom dans la bonne liste ci-dessous
//  Rien d'autre à toucher dans le code.
// ═══════════════════════════════════════════════════════════════
//
// Nommage : minuscules, tirets, sans espace ni accent.
// Format  : .mp4 en H.264 — c'est ce que lisent tous les navigateurs.
//
// ⚠️ Les fichiers de public/ ne reçoivent pas d'empreinte de version dans leur
// nom. Si tu remplaces le contenu d'une vidéo en gardant le même nom, les
// visiteurs qui l'ont en cache garderont l'ancienne. Donne un nouveau nom
// (home-2b.mp4) plutôt que d'écraser.

// Écrans larges — vidéos horizontales.
export const HOME_VIDEOS = ["home-1.mp4"];

// Téléphones — vidéos verticales. Liste vide = on retombe sur la liste ci-dessus.
export const HOME_VIDEOS_MOBILE = [
  "homephone1.mp4",
  "homephone2.mp4",
  "homephone3.mp4",
];

// ═══════════════════════════════════════════════════════════════
//  VIDÉOS QUI DEMANDENT UN TEXTE SOMBRE
//  Par défaut le logo et le menu sont clairs sur la vidéo. Une vidéo à
//  dominante claire a besoin de l'inverse : ajouter son nom de fichier ici
//  et le texte, le logo et la barre légale basculeront en sombre pour elle.
// ═══════════════════════════════════════════════════════════════
export const VIDEOS_WITH_DARK_TEXT = ["homephone3.mp4"];

// Seuil de bascule. Doit rester identique au breakpoint de Home.css.
export const MOBILE_QUERY = "(max-width: 520px)";

// Dossier des vidéos, relatif à la racine publique du site.
const VIDEOS_DIR = "videos";

/**
 * Tire une entrée au hasard. Renvoie null si la liste est vide.
 * Avec une seule entrée, Math.random() * 1 donne toujours l'index 0 : le cas
 * « une seule vidéo » se règle tout seul, sans condition particulière.
 */
function pickOne(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list[Math.floor(Math.random() * list.length)];
}

/**
 * Construit l'URL publique d'une vidéo.
 * encodeURIComponent protège les noms qui contiendraient un espace ou un
 * accent — ça ne devrait pas arriver vu la convention ci-dessus, mais un nom
 * mal formé casserait l'URL en silence.
 */
function toPublicUrl(filename) {
  if (!filename) return null;
  const base = process.env.PUBLIC_URL || "";
  return `${base}/${VIDEOS_DIR}/${encodeURIComponent(filename)}`;
}

// ⚠️ Les tirages sont faits ICI, au niveau du module, et pas dans le composant
// Home. Un module n'est évalué qu'une fois par chargement de page : les vidéos
// choisies restent donc les mêmes tant que l'utilisateur navigue, et changent
// au rafraîchissement. Si le tirage vivait dans Home, il serait rejoué à chaque
// retour sur l'accueil, puisque AnimatePresence démonte et remonte les pages.
//
// On tire une vidéo dans CHAQUE liste, mais une seule sera référencée par le
// <video> selon la largeur d'écran : le navigateur ne télécharge que celle-là.
const chosenDesktop = pickOne(HOME_VIDEOS);
const chosenMobile = pickOne(HOME_VIDEOS_MOBILE);

export const HOME_VIDEO_URL = toPublicUrl(chosenDesktop);
export const HOME_VIDEO_MOBILE_URL = toPublicUrl(chosenMobile);

const DESKTOP_DARK_TEXT = VIDEOS_WITH_DARK_TEXT.includes(chosenDesktop);
const MOBILE_DARK_TEXT = VIDEOS_WITH_DARK_TEXT.includes(chosenMobile);

/**
 * Quelle vidéo est à l'écran, et comment habiller le texte par-dessus.
 * Utilisé par Home (logo, menu) et par App (barre légale), pour que les deux
 * s'accordent toujours sur la même vidéo.
 *
 * On écoute les changements de largeur pour couvrir la rotation du téléphone
 * et le redimensionnement de fenêtre.
 */
export function useHomeVideo() {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Une seule URL est posée dans le <video>, donc une seule vidéo est
  // téléchargée. Repli en cascade si une liste est vide.
  const url =
    (isMobile ? HOME_VIDEO_MOBILE_URL : HOME_VIDEO_URL) || HOME_VIDEO_URL;

  return {
    url,
    darkText: isMobile ? MOBILE_DARK_TEXT : DESKTOP_DARK_TEXT,
  };
}
